/**
 * useForms Hook
 *
 * Wraps useListPublicFormsQuery, useGetMyFormSubmissionsQuery, and useSubmitFormResponseMutation
 * for the customer-facing forms feature.
 *
 * Requirements: Task 12 - Forms Pages
 */

import { useMemo, useCallback } from 'react';
import {
  useListPublicFormsQuery,
  useGetMyFormSubmissionsQuery,
  useSubmitFormResponseMutation,
  type ListPublicFormsQuery,
  type GetMyFormSubmissionsQuery,
  type PaginationInput,
} from '@bloomcode/graphql';

// Type aliases for query results
type FormItem = ListPublicFormsQuery['listPublicForms']['data'][number];
type FormPaginationInfo = ListPublicFormsQuery['listPublicForms']['pagination'];
type SubmissionItem = GetMyFormSubmissionsQuery['getMyFormSubmissions']['data'][number];
type SubmissionPaginationInfo = GetMyFormSubmissionsQuery['getMyFormSubmissions']['pagination'];

// ============================================================================
// PUBLIC FORMS LIST HOOK
// ============================================================================

interface UsePublicFormsOptions {
  page?: number;
  limit?: number;
  skip?: boolean;
}

interface UsePublicFormsResult {
  forms: FormItem[];
  pagination: FormPaginationInfo | null;
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch available public forms with pagination
 */
export function usePublicForms(options: UsePublicFormsOptions = {}): UsePublicFormsResult {
  const { page = 1, limit = 10, skip = false } = options;

  const pagination: PaginationInput = { page, limit };

  const {
    data,
    loading,
    error,
    refetch: refetchQuery,
  } = useListPublicFormsQuery({
    variables: { pagination },
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const forms: FormItem[] = useMemo(() => data?.listPublicForms?.data ?? [], [data]);

  const paginationInfo: FormPaginationInfo | null = useMemo(
    () => data?.listPublicForms?.pagination ?? null,
    [data]
  );

  const totalCount = paginationInfo?.total ?? 0;

  const refetch = useCallback(() => {
    refetchQuery();
  }, [refetchQuery]);

  return {
    forms,
    pagination: paginationInfo,
    totalCount,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

// ============================================================================
// FORM SUBMISSIONS HOOK
// ============================================================================

interface UseMyFormSubmissionsOptions {
  formId?: string;
  page?: number;
  limit?: number;
  skip?: boolean;
}

interface UseMyFormSubmissionsResult {
  submissions: SubmissionItem[];
  pagination: SubmissionPaginationInfo | null;
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch the current customer's form submissions with optional formId filter
 */
export function useMyFormSubmissions(
  options: UseMyFormSubmissionsOptions = {}
): UseMyFormSubmissionsResult {
  const { formId, page = 1, limit = 10, skip = false } = options;

  const pagination: PaginationInput = { page, limit };

  const {
    data,
    loading,
    error,
    refetch: refetchQuery,
  } = useGetMyFormSubmissionsQuery({
    variables: { formId, pagination },
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const submissions: SubmissionItem[] = useMemo(
    () => data?.getMyFormSubmissions?.data ?? [],
    [data]
  );

  const paginationInfo: SubmissionPaginationInfo | null = useMemo(
    () => data?.getMyFormSubmissions?.pagination ?? null,
    [data]
  );

  const totalCount = paginationInfo?.total ?? 0;

  const refetch = useCallback(() => {
    refetchQuery();
  }, [refetchQuery]);

  return {
    submissions,
    pagination: paginationInfo,
    totalCount,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

// ============================================================================
// SUBMIT FORM RESPONSE HOOK
// ============================================================================

interface UseSubmitFormResponseResult {
  submitForm: (input: {
    formId: string;
    data: Record<string, unknown>;
    source?: string;
  }) => Promise<{
    success: boolean;
    message?: string;
  }>;
  isSubmitting: boolean;
}

/**
 * Hook to submit a form response
 */
export function useSubmitFormResponse(): UseSubmitFormResponseResult {
  const [submitFormMutation, { loading }] = useSubmitFormResponseMutation();

  const submitForm = useCallback(
    async (input: { formId: string; data: Record<string, unknown>; source?: string }) => {
      try {
        const result = await submitFormMutation({
          variables: {
            input: {
              formId: input.formId,
              data: input.data,
              source: input.source ?? 'portal',
            },
          },
          refetchQueries: ['GetMyFormSubmissions'],
        });

        if (result.data?.submitFormResponse?.success) {
          return { success: true, message: result.data.submitFormResponse.message };
        }

        return {
          success: false,
          message: result.data?.submitFormResponse?.message ?? 'Submission failed',
        };
      } catch (err) {
        return {
          success: false,
          message: err instanceof Error ? err.message : 'Network error',
        };
      }
    },
    [submitFormMutation]
  );

  return {
    submitForm,
    isSubmitting: loading,
  };
}

export type { FormItem, SubmissionItem, FormPaginationInfo, SubmissionPaginationInfo };
